import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { isDevelopmentEnv } from '../config/runtimeConfig';

interface Props {
  children: ReactNode;
  /** Fallback when used at route level (e.g. "This page" vs "The app") */
  level?: 'app' | 'route';
  /** Optional custom fallback UI */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary: catches JavaScript errors in the child tree and shows a fallback UI.
 * Use at app level (index.tsx) and at route level (App.tsx) so one route failing doesn't break the whole app.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    if (typeof console !== 'undefined' && console.error) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      const isApp = this.props.level === 'app';
      return (
        <div className="min-h-screen bg-red5-dark flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-red5-card-dark border border-red5-border rounded-lg shadow-xl p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-red5-red/20 border border-red5-red">
                <AlertTriangle className="h-10 w-10 text-red5-red" aria-hidden />
              </div>
            </div>
            <h1 className="text-xl font-bold text-white mb-2">
              {isApp ? 'Something went wrong' : 'Something went wrong on this page'}
            </h1>
            <p className="text-white opacity-80 text-sm mb-4">
              {isApp
                ? 'The application encountered an error. You can try again or go to the dashboard.'
                : 'This page encountered an error. You can try again or go back to the dashboard.'}
            </p>
            {isDevelopmentEnv() && this.state.error && (
              <details className="text-left mb-4 p-3 bg-black/30 rounded border border-red5-border">
                <summary className="text-white opacity-70 text-sm cursor-pointer">
                  Error details
                </summary>
                <pre className="mt-2 text-xs text-red5-red overflow-auto max-h-32 break-all whitespace-pre-wrap">
                  {this.state.error.toString()}
                </pre>
                {this.state.errorInfo?.componentStack && (
                  <pre className="mt-2 text-xs text-white opacity-60 overflow-auto max-h-24">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={this.handleRetry}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red5-red text-white font-medium hover:opacity-90 transition-opacity"
              >
                <RefreshCw className="h-4 w-4" />
                Try again
              </button>
              {!isApp && (
                <Link
                  to="/dashboard"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-red5-border text-white font-medium hover:bg-red5-card-darker transition-colors"
                >
                  <Home className="h-4 w-4" />
                  Back to dashboard
                </Link>
              )}
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
